import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { projectsService } from '@/services/projectsService'
import { financeService } from '@/services/financeService'
import {
  Project,
  CostLocation,
  ProfitCenter,
  FinanceCustomer,
} from '@/api/types'

export const ProjectsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  const [project, setProject] = useState<Project>({
    projectId: 0,
    id: 0,
    code: '',
    name: '',
    ref: '',
    reference: '',
    description: '',
    isActive: true,
    costProfitCenter: 0,
    costLocation: 0,
    customerId: 0,
    projectValue: 0,
    projectValueExVat: 0,
    startDate: '',
    duration: 0,
    durationDays: 0,
    projectBilled: 0,
    projectBalance: 0,
    projectDue: 0,
    additionalBillValue: 0,
    companyId: companyId,
    branchId: branchId || 0,
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [costLocations, setCostLocations] = useState<CostLocation[]>([])
  const [profitCenters, setProfitCenters] = useState<ProfitCenter[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, costLocationsData, profitCentersData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          projectsService.getCostLocations(),
          projectsService.getProfitCenters(),
        ])

        setCustomers(customersData || [])
        setCostLocations(costLocationsData || [])
        setProfitCenters(profitCentersData || [])
      } catch (error: any) {
        console.error('Failed to load project lookups', error)
        await showError('Error', error.response?.data?.message || t('projects.failedToLoadProjectLookups'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load project data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadProject = async () => {
      try {
        setLoading(true)
        const projectId = Number(id)
        const projectData = await projectsService.getProjectById(projectId, branchId)
        console.log('Loaded project data:', projectData)
        console.log('Cost Profit Center:', projectData.costProfitCenter)
        console.log('Cost Location:', projectData.costLocation)
        setProject(projectData)
      } catch (error: any) {
        console.error('Failed to load project data', error)
        await showError('Error', error.message || t('projects.failedToLoadProject'))
      } finally {
        setLoading(false)
      }
    }

    loadProject()
  }, [isEditMode, branchId, id])

  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return ''
    try {
      if (dateString.includes('T')) {
        return new Date(dateString).toISOString().split('T')[0]
      }
      return dateString.split('T')[0]
    } catch {
      return dateString
    }
  }

  const calculateEndDate = (startDate: string, duration: number): string => {
    if (!startDate || !duration) return ''
    try {
      const start = new Date(startDate)
      start.setDate(start.getDate() + duration)
      return start.toISOString().split('T')[0]
    } catch {
      return ''
    }
  }

  const handleDurationChange = (duration: number) => {
    setProject((prev) => {
      const updated = { ...prev, duration, durationDays: duration }
      if (updated.startDate) {
        updated.endDate = calculateEndDate(updated.startDate, duration)
      }
      return updated as Project
    })
  }

  const handleStartDateChange = (startDate: string) => {
    setProject((prev) => {
      const updated = { ...prev, startDate }
      if (updated.duration) {
        updated.endDate = calculateEndDate(startDate, updated.duration)
      }
      return updated as Project
    })
  }

  const isFormValid = () => {
    if (!project.name || project.name.trim() === '') return false
    if (!project.customerId || project.customerId === 0) return false
    return true
  }

  // Get user IP address (logged-in user's IP, not server's IP)
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('projects.pleaseSelectBranchFirst'))
      return
    }

    if (!isFormValid()) {
      await showValidationError(t('projects.pleaseFillAllRequiredFields'))
      return
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Project...' : 'Saving Project...',
        'Please wait while we process your request'
      )
      
      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Transform to API structure
      const projectToSave = {
        id: isEditMode ? (project.projectId || project.id || 0) : 0,
        name: project.name || '',
        description: project.description || project.ref || project.reference || '',
        costProfitCenterId: project.costProfitCenter || 0,
        costLocationId: project.costLocation || 0,
        projectValue: project.projectValue || project.projectValueExVat || 0,
        customerId: project.customerId || 0,
        startDate: project.startDate ? new Date(project.startDate).toISOString() : new Date().toISOString(),
        durationDays: project.durationDays || project.duration || 0,
        isActive: project.isActive !== undefined ? project.isActive : true,
        branchId: branchId,
        ipAddress: ipAddress, // Set logged-in user's IP address
      }

      // Log the data structure being sent
      console.log('=== Project Save Data Structure ===')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'CREATE')
      console.log('API Payload:', JSON.stringify(projectToSave, null, 2))
      console.log('===================================')

      if (isEditMode) {
        console.log('Calling updateProject API...')
        await projectsService.updateProject(projectToSave)
      } else {
        console.log('Calling saveProject API...')
        await projectsService.saveProject(projectToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('projects.projectUpdatedSuccess') : t('projects.projectCreatedSuccess')
      )

      navigate('/projects')
    } catch (error: any) {
      console.error('Failed to save project', error)
      console.error('Error response:', error.response?.data)
      console.error('Error message:', error.message)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = customers.map((customer) => ({
    value: String(customer.customerId),
    label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
  }))

  const costLocationOptions: SearchableSelectOption[] = costLocations.map((location) => ({
    value: String(location.id || location.costLocationId || 0),
    label: location.name || location.costLocationName || `Location ${location.id || location.costLocationId}`,
  }))

  const profitCenterOptions: SearchableSelectOption[] = profitCenters.map((center) => ({
    value: String(center.id || center.profitCenterId || 0),
    label: center.name || center.profitCenterName || `Center ${center.id || center.profitCenterId}`,
  }))

  if (loading && !project.name) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('projects.title')}
        description={isEditMode ? t('projects.updateProjectDetails') : t('projects.createNewProject')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('projects.projectInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {isEditMode && (
              <div className="space-y-2">
                <Label>{t('projects.code')}</Label>
                <Input value={project.code || ''} readOnly className="bg-muted" />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('projects.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={project.customerId ? String(project.customerId) : ''}
                onValueChange={(value) => setProject((prev) => ({ ...prev, customerId: Number(value) || 0 }))}
                placeholder={t('projects.selectCustomer')}
                searchPlaceholder={t('projects.searchCustomers')}
                emptyMessage={t('projects.noCustomersFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.name')}</Label>
              <Input
                value={project.name || ''}
                onChange={(e) => setProject((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('projects.enterProjectName')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.projectReference')}</Label>
              <Input
                value={project.ref || project.reference || project.description || ''}
                onChange={(e) => setProject((prev) => ({ 
                  ...prev, 
                  ref: e.target.value, 
                  reference: e.target.value,
                  description: e.target.value,
                }))}
                placeholder={t('projects.enterProjectReference')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.status')}</Label>
              <Select
                value={project.isActive ? 'Yes' : 'No'}
                onValueChange={(value) => setProject((prev) => ({ ...prev, isActive: value === 'Yes' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cost Profit Center</Label>
              <SearchableSelect
                options={profitCenterOptions}
                value={project.costProfitCenter ? String(project.costProfitCenter) : ''}
                onValueChange={(value) => setProject((prev) => ({ ...prev, costProfitCenter: Number(value) || 0 }))}
                placeholder={t('projects.selectProfitCenter')}
                searchPlaceholder={t('projects.searchProfitCenters')}
                emptyMessage={t('projects.noProfitCentersFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.costLocation')}</Label>
              <SearchableSelect
                options={costLocationOptions}
                value={project.costLocation ? String(project.costLocation) : ''}
                onValueChange={(value) => setProject((prev) => ({ ...prev, costLocation: Number(value) || 0 }))}
                placeholder={t('projects.selectCostLocation')}
                searchPlaceholder={t('projects.searchCostLocations')}
                emptyMessage={t('projects.noCostLocationsFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.projectValueExVat')}</Label>
              <Input
                type="number"
                step="0.01"
                value={project.projectValueExVat || project.projectValue || ''}
                onChange={(e) => setProject((prev) => ({
                  ...prev,
                  projectValue: Number(e.target.value) || 0,
                  projectValueExVat: Number(e.target.value) || 0,
                }))}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.startDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(project.startDate)}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.durationDays')}</Label>
              <Input
                type="number"
                value={project.duration || project.durationDays || ''}
                onChange={(e) => handleDurationChange(Number(e.target.value) || 0)}
                placeholder="0"
              />
            </div>
            {isEditMode && (
              <>
                <div className="space-y-2">
                  <Label>{t('projects.additionalBillValue')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={project.additionalBillValue || ''}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('projects.projectBilled')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={project.projectBilled || ''}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('projects.projectBalance')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={project.projectBalance || ''}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('projects.endDate')}</Label>
                  <Input
                    type="date"
                    value={formatDateForInput(project.endDate)}
                    readOnly
                    className="bg-muted"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/projects')} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || !isFormValid()}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('projects.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('projects.updateProject') : t('projects.saveProject')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

